import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { useEffect } from 'react';
import { fetchDocumentCategories } from '@reducers/v2/imageHandlingV2/thunks';
import { ImageHandlingHeaderProps } from '@v2/components/imageHandling/components/Header/Header';
import { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types.d';
import { useManageAttachments } from '@components/common/attachButton/hooks/useManageAttachments';
import { useManageImageHandlingHeaderActions } from './useManageImageHandlingHeaderActions';
import { useImageHandlingChecks } from '@v2/components/imageHandling/hooks/useImageHandlingChecks';
import { useSelector } from 'react-redux';
import {
  isImageContextHasScopeIdAndScopeReferenceId,
  selectCurrentImageDocumentContext,
} from '../../../../../../../reducers/v2/imageHandlingV2/imageHandlingV2.slice';

interface UseManageImageHandlingHeaderParams
  extends Pick<ImageHandlingHeaderProps, 'images' | 'selectedIndex' | 'getInfoPopupActionsConfig' | 'cropImage' | 'withoutFavoriteButton'> {
  img: ImageDocumentStoreItem | null;
}

export const useManageImageHandlingHeader = ({
  img,
  images,
  selectedIndex,
  getInfoPopupActionsConfig,
  cropImage,
  withoutFavoriteButton,
}: UseManageImageHandlingHeaderParams) => {
  const dispatch = useAppDispatch();

  const imageContext = useSelector(selectCurrentImageDocumentContext);
  const hasContextScopeIdAndScopeReferenceId = isImageContextHasScopeIdAndScopeReferenceId(imageContext);

  const shouldDisplayFavoriteButton = hasContextScopeIdAndScopeReferenceId && !withoutFavoriteButton;

  const {
    attachmentToRemove,
    handleConfirmRemoveAttachment,
    handleToggleFavoriteLink,
    handleStartRemovingAttachment,
    setAttachmentToRemove,
    setShowAttachModal,
    setShowRemoveAttachmentModal,
    showAttachModal,
    showRemoveAttachmentModal,
  } = useManageAttachments({ img });

  const { showCrop, showInfoPopUp, showRotatePopUp, triggerCrop, triggerInfoPopUp, triggerRotatePopUp } = useManageImageHandlingHeaderActions({
    images,
    selectedIndex,
  });

  const { isSingleImage } = useImageHandlingChecks({ images });

  useEffect(() => {
    dispatch(fetchDocumentCategories());
  }, []);

  const toggleAttachModal = () => {
    if (showInfoPopUp) {
      triggerInfoPopUp();
    }
    setShowAttachModal(!showAttachModal);
  };

  const download = () => {
    fetch(images[selectedIndex].downloadUrl, { ignoreInterceptor: 'true' })
      .then((response) => response.blob())
      .then((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = images[selectedIndex].fileName;
        link.click();
      })
      .catch(console.error);
  };

  const onSaveCrop = (image: ImageDocumentStoreItem) => {
    triggerCrop();
    cropImage(image);
  };

  const infoPopupActions = getInfoPopupActionsConfig ? getInfoPopupActionsConfig({ toggleAttachModal }) : null;

  return {
    toggleAttachModal,
    handleRemoveAttachment: handleStartRemovingAttachment,
    handleConfirmRemoveAttachment,
    download,
    showAttachModal,
    setShowAttachModal,
    showRemoveAttachmentModal,
    setShowRemoveAttachmentModal,
    attachmentToRemove,
    setAttachmentToRemove,
    showInfoPopUp,
    triggerInfoPopUp,
    showCrop,
    triggerCrop,
    showRotatePopUp,
    triggerRotatePopUp,
    isSingleImage,
    handleToggleFavoriteLink,
    infoPopupActions,
    onSaveCrop,
    imageContext,
    shouldDisplayFavoriteButton,
  };
};
